import { Component, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';

import { LabelBadge } from '../../../shared/components/label-badge/label-badge';
import { FeedPage } from './feed-page';

/**
 * Header of every feed page: title, status badge (+ rejection message), meta
 * line, Delete feed / Save. No band of its own — it sits on the page
 * background, aligned to the same centred `max-w-[1180px]` column as the
 * cards below it, and stays put above the scrolling body. Type pages project extra badges
 * next to the title via `<ng-container feedBadges>` (e.g. the SIRI flavor).
 *
 * Default change detection on purpose: Save's disabled state comes from
 * `FeedPage.isValid`, which reads plain ngModel fields on some type pages
 * that no signal would notify an OnPush view about.
 */
@Component({
  selector: 'app-feed-page-header',
  standalone: true,
  imports: [TranslocoDirective, DatePipe, LabelBadge],
  template: `
    <ng-container *transloco="let t; read: 'providers.feedPage'">
      <div class="flex-none px-8 pt-2.5">
        <div class="mx-auto flex max-w-[1180px] flex-wrap items-start justify-between gap-4">
          <div class="flex flex-col gap-2">
            <div class="flex items-center gap-3.5">
              <h1 class="m-0 font-display text-2xl font-bold text-ink-1">
                {{ page.isCreate ? t('createTitle') : (page.feed()?.name || page.name() || t('createTitle')) }}
              </h1>
              <ng-content select="[feedBadges]" />
              @if (page.feedStatus(); as status) {
                <app-label-badge [dot]="page.feedStatusDot()">{{ t('status.' + status) }}</app-label-badge>
              }
            </div>
            @if (page.feedStatus() === 'REJECTED' && page.feed()?.statusMessage) {
              <p class="m-0 text-[13px] text-danger">{{ t('status.rejectedMessage', { message: page.feed()?.statusMessage }) }}</p>
            }
            @if (!page.isCreate) {
              <div class="flex flex-wrap items-center gap-x-6 gap-y-1.5 text-[13px] text-ink-3">
                <span>{{ t('meta.tenant') }} <span class="text-ink-1">{{ page.tenantName() }}</span></span>
                <span>{{ t('meta.provider') }} <span class="text-ink-1">{{ page.provider()?.name }}</span></span>
                @if (page.feed(); as f) {
                  <span>{{ t('meta.created') }} <span class="text-ink-1">{{ f.createdAt | date: 'mediumDate' }}</span></span>
                }
              </div>
            }
          </div>
          <div class="flex flex-none items-center gap-2.5">
            @if (!page.isCreate) {
              <button
                type="button"
                (click)="page.confirmDelete()"
                class="rounded-control border border-hairline bg-card px-4 py-2.5 text-[14px] font-semibold text-danger hover:bg-sunken"
              >
                {{ t('actions.deleteFeed') }}
              </button>
            }
            <button
              type="button"
              (click)="page.save()"
              [disabled]="!page.isValid || page.isSaving()"
              class="rounded-control bg-action px-4 py-2.5 text-[14px] font-semibold text-action-text disabled:opacity-40 hover:brightness-90"
            >
              {{ page.isCreate ? t('actions.create') : t('actions.save') }}
            </button>
          </div>
        </div>
      </div>
    </ng-container>
  `,
  host: { class: 'block flex-none' },
})
export class FeedPageHeaderComponent {
  protected readonly page = inject(FeedPage);
}
